import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { shopRepository } from "../data/shopRepository";
import type { ImportBatch, ImportBatchInput, Shop, ShopCreateInput, ShopUpdateInput } from "../types";

const SHOPS_KEY = ["shops"] as const;
const IMPORT_BATCHES_KEY = ["import-batches"] as const;

function toErrorMessage(...errors: Array<unknown>): string | null {
  for (const error of errors) {
    if (error instanceof Error) {
      return error.message;
    }
  }
  return null;
}

export function useShops() {
  const queryClient = useQueryClient();

  const shopsQuery = useQuery({
    queryKey: SHOPS_KEY,
    queryFn: () => shopRepository.listShops()
  });

  const batchesQuery = useQuery({
    queryKey: IMPORT_BATCHES_KEY,
    queryFn: () => shopRepository.listImportBatches()
  });

  const invalidateShops = () => queryClient.invalidateQueries({ queryKey: SHOPS_KEY });
  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: SHOPS_KEY }),
      queryClient.invalidateQueries({ queryKey: IMPORT_BATCHES_KEY })
    ]);
  };

  const createMutation = useMutation({
    mutationFn: (input: ShopCreateInput) => shopRepository.createShop(input),
    onSuccess: invalidateShops
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: ShopUpdateInput }) =>
      shopRepository.updateShop(id, input),
    onSuccess: invalidateShops
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => shopRepository.deleteShop(id),
    onSuccess: invalidateShops
  });

  const importMutation = useMutation({
    mutationFn: ({ inputs, batch }: { inputs: ShopCreateInput[]; batch: ImportBatchInput }) =>
      shopRepository.importShops(inputs, batch),
    onSuccess: invalidateAll
  });

  const actions = useMemo(
    () => ({
      async createShop(input: ShopCreateInput): Promise<Shop> {
        return createMutation.mutateAsync(input);
      },
      async updateShop(id: string, input: ShopUpdateInput): Promise<Shop> {
        return updateMutation.mutateAsync({ id, input });
      },
      async deleteShop(id: string): Promise<void> {
        await deleteMutation.mutateAsync(id);
      },
      async importShops(inputs: ShopCreateInput[], batch: ImportBatchInput): Promise<ImportBatch> {
        return importMutation.mutateAsync({ inputs, batch });
      },
      async refresh(): Promise<void> {
        await invalidateAll();
      }
    }),
    // Mutation objects and the query client are stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    shops: shopsQuery.data ?? [],
    importBatches: batchesQuery.data ?? [],
    isLoading: shopsQuery.isPending || batchesQuery.isPending,
    error: toErrorMessage(shopsQuery.error, batchesQuery.error),
    ...actions
  };
}
